import type { RowSelectionState } from "@tanstack/react-table";
import { useEffect, type RefObject } from "react";

interface UseTableScrollOptions {
  rootRef: RefObject<HTMLDivElement | null>;
  viewportRef: RefObject<HTMLDivElement | null>;
  headerScrollRef: RefObject<HTMLDivElement | null>;
  scrollResetKey?: string | number;
  selectedRowId?: string | null;
  rowSelection?: RowSelectionState;
  isLoading: boolean;
}

export const useTableScroll = ({
  rootRef,
  viewportRef,
  headerScrollRef,
  scrollResetKey,
  selectedRowId,
  rowSelection,
  isLoading,
}: UseTableScrollOptions) => {
  useEffect(() => {
    if (!viewportRef.current) return;

    viewportRef.current.scrollTop = 0;
  }, [scrollResetKey]);

  useEffect(() => {
    if (!viewportRef.current) return;

    if (!selectedRowId) return;

    const selectedRow = viewportRef.current.querySelector(
      `[data-state="selected"]`,
    );
    if (!selectedRow) return;

    const viewport = viewportRef.current;
    const rowRect = selectedRow.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();

    if (rowRect.top < viewportRect.top) {
      viewport.scrollTop += rowRect.top - viewportRect.top;
    } else if (rowRect.bottom > viewportRect.bottom) {
      viewport.scrollTop += rowRect.bottom - viewportRect.bottom;
    }
  }, [selectedRowId, rowSelection, isLoading]);

  useEffect(() => {
    const viewport = viewportRef.current;
    const header = headerScrollRef.current;
    if (!viewport || !header) return;

    const onScroll = () => {
      if (header.scrollLeft !== viewport.scrollLeft) {
        header.scrollLeft = viewport.scrollLeft;
      }
      const root = rootRef.current;
      if (!root) return;
      const { scrollLeft, clientWidth, scrollWidth } = viewport;
      root.toggleAttribute("data-scrolled-left", scrollLeft > 1);
      root.toggleAttribute(
        "data-scrolled-right",
        scrollLeft + clientWidth < scrollWidth - 1,
      );
    };
    const onHeaderWheel = (event: WheelEvent) => {
      if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
        viewport.scrollLeft += event.deltaX;
      }
    };

    onScroll();
    viewport.addEventListener("scroll", onScroll, { passive: true });
    header.addEventListener("wheel", onHeaderWheel, { passive: true });
    const observer =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(onScroll);
    observer?.observe(viewport);
    if (viewport.firstElementChild) {
      observer?.observe(viewport.firstElementChild);
    }

    return () => {
      viewport.removeEventListener("scroll", onScroll);
      header.removeEventListener("wheel", onHeaderWheel);
      observer?.disconnect();
    };
  }, []);
};
